import { DateTime } from '@cheqroom/date-time';

import { MAX_QUERYSTRING_LENGTH } from '../core/constants';
import { HttpErrorLike, mapHttpErrorToResourceError } from '../core/errors';
import { DocumentType, PythonSearchVariables, ResourceSortState, TableQueryFilters } from '../core/types';

/** Shared helpers for resource hooks. */

/** fromBetween: DateTime → startOfDay UTC ISO; string unchanged. */
export function serializeFromBetweenForSearch(value: DateTime | string | undefined): string | undefined {
	if (value == null) return undefined;
	if (typeof value === 'string') return value;
	return value.startOfDay().toFloatingISOString();
}

/** toBetween: DateTime → endOfDay UTC ISO; string unchanged. */
export function serializeToBetweenForSearch(value: DateTime | string | undefined): string | undefined {
	if (value == null) return undefined;
	if (typeof value === 'string') return value;
	return value.endOfDay().toFloatingISOString();
}

/** ResourceSortState → Python _sort string (e.g. 'name,-created'). Optional sortFieldMapper for field id mapping. */
export const convertSortToPython = (sort: ResourceSortState, sortFieldMapper?: (fieldId: string) => string): string => {
	if (!sort || sort.length === 0) {
		return '';
	}

	return sort
		.map((sortItem) => {
			const fieldId = sortFieldMapper ? sortFieldMapper(sortItem.id) : sortItem.id;
			return sortItem.desc ? `-${fieldId}` : fieldId;
		})
		.join(',');
};

/** Build common search params from filters and fields (list, search, sort, customFieldFilters, _fields). Leaves _limit/_skip to caller. */
export const buildCommonSearchVariables = (
	filters: Partial<TableQueryFilters> | undefined,
	fields: readonly string[],
	defaultFields: readonly string[] | undefined,
	fieldMapper: ((fields: readonly string[]) => string[]) | undefined,
	sortFieldMapper: ((fieldId: string) => string) | undefined,
	defaultSearchParams: Record<string, unknown> | undefined
): Partial<PythonSearchVariables> => {
	const searchVariables: Partial<PythonSearchVariables> = {
		...defaultSearchParams,
	};

	if (filters?.list) {
		searchVariables.listName = filters.list;
	}

	if (filters?.search) {
		searchVariables.query = filters.search;
	}

	if (filters?.sort && filters.sort.length > 0) {
		const sortString = convertSortToPython(filters.sort, sortFieldMapper);
		if (sortString) {
			searchVariables._sort = sortString;
		}
	}

	let fieldsToRequest: readonly string[] = [];
	if (fields.length > 0) {
		fieldsToRequest = fieldMapper ? fieldMapper(fields) : fields;
	} else if (defaultFields && defaultFields.length > 0) {
		fieldsToRequest = defaultFields;
	}

	const fieldsString = buildFieldsString(fieldsToRequest);
	if (fieldsString !== '*') {
		searchVariables._fields = fieldsString;
	}

	if (filters?.customFieldFilters) {
		Object.assign(searchVariables, filters.customFieldFilters);
	}

	return searchVariables;
};

/** Fields array → comma-separated string; falls back to defaultFields or '*'. */
export const buildFieldsString = (
	fields?: readonly string[],
	defaultFields?: readonly string[]
): string | undefined => {
	if (fields && fields.length > 0) {
		return fields.join(',');
	}
	if (defaultFields && defaultFields.length > 0) {
		return defaultFields.join(',');
	}
};

/** Add _fields to body when responseFields provided; otherwise return body unchanged. */
export const addResponseFields = <T extends Record<string, unknown>>(
	body: T,
	responseFields?: string[]
): T & { _fields?: string } => {
	if (responseFields && responseFields.length > 0) {
		return {
			...body,
			_fields: responseFields.join(','),
		};
	}
	return body;
};

/** Add _fields to body; use defaultField (default '_id') when responseFields empty. */
export const addResponseFieldsWithDefault = <T extends Record<string, unknown>>(
	body: T,
	responseFields?: string[],
	defaultField = '_id'
): T & { _fields: string } => {
	if (responseFields && responseFields.length > 0) {
		return {
			...body,
			_fields: responseFields.join(','),
		};
	}
	return {
		...body,
		_fields: defaultField,
	};
};

export { MAX_QUERYSTRING_LENGTH, DEFAULT_STALE_TIME } from '../core/constants';

/** True if url + params would exceed MAX_QUERYSTRING_LENGTH (use POST instead of GET). */
export const shouldUsePostForUrl = (url: string, params?: Record<string, unknown>): boolean => {
	if (!params || Object.keys(params).length === 0) {
		return false;
	}

	const queryString = Object.keys(params)
		.map((k) => `${encodeURIComponent(k)}=${encodeURIComponent(String(params[k]))}`)
		.join('&');
	const fullUrl = `${url}?${queryString}`;

	return fullUrl.length >= MAX_QUERYSTRING_LENGTH;
};

/** Normalize resource API errors to typed ResourceApiError; rethrow others. */
export const handleResourceError = (error: unknown): never => {
	if (error instanceof Error && error.message === 'timeout') {
		const timeoutError = Object.assign(new Error('timeout'), {
			name: 'NetworkTimeout',
			status: 408,
		} as HttpErrorLike);
		throw mapHttpErrorToResourceError(timeoutError);
	}
	throw error;
};

export const getEndpointForDocumentType = (documentType: DocumentType) => {
	switch (documentType) {
		case 'checkouts':
			return 'orders';
		case 'users':
			return 'customers';
		default:
			return documentType;
	}
};
